import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTimes, FaTrash, FaPlus, FaMinus, FaShoppingBag,
  FaArrowRight, FaSpinner
} from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';

const CartSidebar = () => {
  const {
    cartItems,
    isCartOpen,
    isLoading,
    setIsCartOpen,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    getCartTotal,
    clearCart,
  } = useCart();
  const { isDarkMode } = useTheme();

  const isMobile = window.innerWidth < 768;

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);

  const subtotal  = getCartTotal();
  const gst       = subtotal * 0.03;
  const grandTotal = subtotal;

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isCartOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1049, backdropFilter: 'blur(5px)' }}
            onClick={() => setIsCartOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        className="cart-sidebar"
        initial={false}
        animate={{ x: isCartOpen ? 0 : '100%' }}
        transition={{ type: 'spring', damping: 20 }}
        style={{
          position: 'fixed',
          top:    isMobile ? 'auto' : 0,
          bottom: isMobile ? 0 : 'auto',
          right: 0,
          width: '100%',
          maxWidth: isMobile ? '100%' : '400px',
          height:   isMobile ? '85vh' : '100vh',
          background: isDarkMode ? 'var(--bg-secondary)' : 'white',
          boxShadow: '-5px 0 15px rgba(128,0,32,0.15)',
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isMobile ? '25px 25px 0 0' : '0',
        }}
      >

        {/* ── Header ── */}
        <div className="p-4 border-bottom" style={{
          background: isDarkMode ? 'var(--bg-primary)' : 'var(--bg-secondary)'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0 d-flex align-items-center">
              <FaShoppingBag className="me-2" style={{ color: 'var(--primary-gold)' }} />
              Shopping Cart ({cartItems.length})
            </h5>
            <button
              className="btn btn-link p-0"
              onClick={() => setIsCartOpen(false)}
              style={{ color: 'var(--text-primary)', fontSize: '24px' }}
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-grow-1 overflow-auto p-4">

          {/* Loading spinner */}
          {isLoading && (
            <div className="text-center py-5">
              <FaSpinner style={{
                fontSize: 36,
                color: 'var(--primary-gold)',
                animation: 'spin 1s linear infinite',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p className="mt-3 text-muted" style={{ fontSize: 13 }}>Loading cart…</p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && cartItems.length === 0 && (
            <div className="text-center py-5">
              <FaShoppingBag style={{ fontSize: '80px', color: 'var(--text-light)', opacity: 0.3 }} />
              <h6 className="mt-4 mb-3">Your cart is empty</h6>
              <p className="text-muted mb-4">Add some beautiful jewellery to your cart</p>
              <Link to="/collections" className="btn btn-luxury" onClick={() => setIsCartOpen(false)}>
                Start Shopping
              </Link>
            </div>
          )}

          {/* Items */}
          {!isLoading && (
            <AnimatePresence>
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.cart_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="card mb-3 border-0"
                  style={{
                    background: isDarkMode ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
                    borderRadius: '10px',
                  }}
                >
                  <div className="card-body p-3">
                    <div className="row g-3">

                      {/* Image */}
                      <div className="col-4">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="img-fluid rounded"
                          style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=No+Image'; }}
                        />
                      </div>

                      {/* Details */}
                      <div className="col-8">
                        <div className="d-flex justify-content-between">
                          <h6 className="mb-1" style={{ fontSize: '14px', fontWeight: '600' }}>
                            {item.name}
                          </h6>
                          <button
                            className="btn btn-link p-0 text-danger"
                            onClick={() => removeFromCart(item.product_id)}
                            style={{ fontSize: '16px' }}
                          >
                            <FaTrash />
                          </button>
                        </div>

                        {/* Stock warning */}
                        {item.stock <= 5 && (
                          <small style={{ color: '#ff9800', fontSize: 11 }}>
                            Only {item.stock} left in stock
                          </small>
                        )}

                        <div className="d-flex justify-content-between align-items-center mt-2">

                          {/* ── Quantity Controls ── */}
                          <div className="d-flex align-items-center gap-2">
                            <button
                              className="btn btn-sm"
                              onClick={() => decreaseQuantity(item.product_id, item.quantity)}
                              style={{
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                width: '30px', height: '30px',
                                padding: 0, borderRadius: '5px',
                              }}
                            >
                              <FaMinus style={{ fontSize: '12px' }} />
                            </button>

                            <span style={{ minWidth: '30px', textAlign: 'center', fontWeight: '600' }}>
                              {item.quantity}
                            </span>

                            <button
                              className="btn btn-sm"
                              onClick={() => increaseQuantity(item.cart_id, item.quantity)}
                              disabled={item.quantity >= item.stock}
                              style={{
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                width: '30px', height: '30px',
                                padding: 0, borderRadius: '5px',
                                opacity: item.quantity >= item.stock ? 0.4 : 1,
                              }}
                            >
                              <FaPlus style={{ fontSize: '12px' }} />
                            </button>
                          </div>

                          {/* Price */}
                          <div className="text-end">
                            <div className="gold-text" style={{ fontWeight: '700', fontSize: '16px' }}>
                              {formatPrice(item.price * item.quantity)}
                            </div>
                            {item.quantity > 1 && (
                              <small className="text-muted">
                                {formatPrice(item.price)} each
                              </small>
                            )}
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* ── Footer ── */}
        {!isLoading && cartItems.length > 0 && (
          <div className="border-top p-4" style={{
            background: isDarkMode ? 'var(--bg-primary)' : 'var(--bg-secondary)'
          }}>
            {/* <div className="d-flex justify-content-between mb-2">
              <span>Subtotal</span>
              <span className="fw-bold">{formatPrice(subtotal)}</span>
            </div>
            <div className="d-flex justify-content-between mb-3">
              <span>GST (3%)</span>
              <span>{formatPrice(gst)}</span>
            </div> */}

            <div className="d-flex justify-content-between mb-4 pb-3 border-bottom">
              <span className="h5 mb-0">Total</span>
              <span className="h5 mb-0 gold-text">{formatPrice(grandTotal)}</span>
            </div>

            <div className="d-grid gap-2">
              <Link
                to="/checkout"
                className="btn btn-luxury d-flex align-items-center justify-content-center"
                onClick={() => setIsCartOpen(false)}
              >
                Proceed to Checkout <FaArrowRight className="ms-2" />
              </Link>
              <Link
                to="/cart"
                className="btn btn-outline-secondary"
                onClick={() => setIsCartOpen(false)}
              >
                View Cart Details
              </Link>
              <button className="btn btn-link text-danger" onClick={clearCart}>
                Clear Cart
              </button>
            </div>
          </div>
        )}

      </motion.div>
    </>
  );
};

export default CartSidebar;